import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { resolveDefaultLocale, type Locale } from './t.js';

type LocaleListener = (locale: Locale) => void;

/**
 * Process-wide source of truth for the current locale.
 *
 * Components subscribe via `LocaleController` (a Lit reactive controller)
 * which mirrors the store value into the component and triggers re-render
 * when the locale changes mid-session.
 */
class LocaleStore {
  private _locale: Locale;
  private listeners = new Set<LocaleListener>();

  constructor(initial: Locale) {
    this._locale = initial;
  }

  get locale(): Locale {
    return this._locale;
  }

  set(locale: Locale): void {
    if (locale === this._locale) return;
    this._locale = locale;
    for (const listener of this.listeners) listener(locale);
  }

  subscribe(listener: LocaleListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

let store: LocaleStore | undefined;

export function initLocaleStore(initial?: Locale): LocaleStore {
  if (store) {
    // Components may construct (and call getLocaleStore) before bootstrap finishes
    // loading the persisted profile. When bootstrap finally calls initLocaleStore
    // with the stored locale, sync it into the existing store.
    if (initial !== undefined) store.set(initial);
    return store;
  }
  const resolved =
    initial ??
    resolveDefaultLocale(
      typeof localStorage !== 'undefined' ? localStorage.getItem('learn-numbers:locale') : null,
      typeof navigator !== 'undefined' ? navigator.language : undefined,
    );
  store = new LocaleStore(resolved);
  return store;
}

export function getLocaleStore(): LocaleStore {
  if (!store) return initLocaleStore();
  return store;
}

/**
 * Reset the store. Test-only.
 */
export function _resetLocaleStoreForTests(): void {
  store = undefined;
}

export class LocaleController implements ReactiveController {
  private host: ReactiveControllerHost;
  private unsubscribe?: () => void;
  private _store = getLocaleStore();

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  get locale(): Locale {
    return this._store.locale;
  }

  setLocale(locale: Locale): void {
    this._store.set(locale);
  }

  hostConnected(): void {
    this.unsubscribe = this._store.subscribe(() => this.host.requestUpdate());
  }

  hostDisconnected(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }
}
